import { type ValueOf } from '~/common/types';

import fileApi from '@/shared/api/file/file';

import { createMemberGuard } from './guard';

type ContentTypeQuery = string | null | undefined;

type FileDownloadOptions = {
  id?: string;
  contentType?: string;
  fileName?: string;
  fileUri: string;
  fileBytes?: string;
  extension?: string | null;
};

export type FileMediaFormat = ValueOf<typeof fileTypes>[number];

export type FileTypeKey = ValueOf<typeof FILE_TYPE_KEYS>;

const FILE_TYPE_KEYS = {
  IMG: 'img',
  MS: 'ms',
  MS_EXCEL: 'ms-excel',
  MS_POWERPOINT: 'ms-powerpoint',
  MS_WORD: 'ms-word',
  PDF: 'pdf',
  TXT: 'txt',
  VIDEO: 'video',
} as const;

const MEDIA_TYPES = {
  APPLICATION: 'application',
  IMAGE: 'image',
  TEXT: 'text',
  VIDEO: 'video',
} as const;

const MEDIA_FORMATS = {
  [MEDIA_TYPES.APPLICATION]: {
    PDF: 'application/pdf',
    VND_MS_EXCEL: 'application/vnd.ms-excel',
    VND_MS_POWERPOINT: 'application/vnd.ms-powerpoint',
    VND_MS_WORD: 'application/msword',
    VND_OPENXMLFORMATS_OFFICEDOCUMENT_PRESENTATIONML_PRESENTATION:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    VND_OPENXMLFORMATS_OFFICEDOCUMENT_SPREADSHEETML_SHEET:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    VND_OPENXMLFORMATS_OFFICEDOCUMENT_WORDPROCESSINGML_DOCUMENT:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  [MEDIA_TYPES.IMAGE]: {
    BMP: 'image/bmp',
    JPEG: 'image/jpeg',
    PNG: 'image/png',
  },
  [MEDIA_TYPES.TEXT]: {
    PLAIN: 'text/plain',
  },
  [MEDIA_TYPES.VIDEO]: {
    MP4: 'video/mp4',
    QUICKTIME: 'video/quicktime',
    WEBM: 'video/webm',
  },
} as const;

const APPLICATION_FORMATS = MEDIA_FORMATS[MEDIA_TYPES.APPLICATION];

const IMAGE_FORMATS = MEDIA_FORMATS[MEDIA_TYPES.IMAGE];

const TEXT_FORMATS = MEDIA_FORMATS[MEDIA_TYPES.TEXT];

const VIDEO_FORMATS = MEDIA_FORMATS[MEDIA_TYPES.VIDEO];

const MEDIA_FORMAT_EXTENSIONS = {
  [APPLICATION_FORMATS.PDF]: ['pdf'],
  [APPLICATION_FORMATS.VND_MS_EXCEL]: ['xls'],
  [APPLICATION_FORMATS.VND_MS_POWERPOINT]: ['ppt'],
  [APPLICATION_FORMATS.VND_MS_WORD]: ['doc'],
  [APPLICATION_FORMATS.VND_OPENXMLFORMATS_OFFICEDOCUMENT_PRESENTATIONML_PRESENTATION]: ['pptx'],
  [APPLICATION_FORMATS.VND_OPENXMLFORMATS_OFFICEDOCUMENT_SPREADSHEETML_SHEET]: ['xlsx'],
  [APPLICATION_FORMATS.VND_OPENXMLFORMATS_OFFICEDOCUMENT_WORDPROCESSINGML_DOCUMENT]: ['docx'],
  [IMAGE_FORMATS.BMP]: ['bmp'],
  [IMAGE_FORMATS.JPEG]: ['jpg', 'jpeg'],
  [IMAGE_FORMATS.PNG]: ['png'],
  [TEXT_FORMATS.PLAIN]: ['txt'],
  [VIDEO_FORMATS.MP4]: ['mp4'],
  [VIDEO_FORMATS.QUICKTIME]: ['mov'],
  [VIDEO_FORMATS.WEBM]: ['webm'],
} as const;

const fileTypes = {
  [FILE_TYPE_KEYS.IMG]: Object.values(IMAGE_FORMATS),
  get [FILE_TYPE_KEYS.MS]() {
    return [
      ...this[FILE_TYPE_KEYS.MS_EXCEL],
      ...this[FILE_TYPE_KEYS.MS_POWERPOINT],
      ...this[FILE_TYPE_KEYS.MS_WORD],
    ];
  },
  [FILE_TYPE_KEYS.MS_EXCEL]: [
    APPLICATION_FORMATS.VND_MS_EXCEL,
    APPLICATION_FORMATS.VND_OPENXMLFORMATS_OFFICEDOCUMENT_SPREADSHEETML_SHEET,
  ],
  [FILE_TYPE_KEYS.MS_POWERPOINT]: [
    APPLICATION_FORMATS.VND_MS_POWERPOINT,
    APPLICATION_FORMATS.VND_OPENXMLFORMATS_OFFICEDOCUMENT_PRESENTATIONML_PRESENTATION,
  ],
  [FILE_TYPE_KEYS.MS_WORD]: [
    APPLICATION_FORMATS.VND_MS_WORD,
    APPLICATION_FORMATS.VND_OPENXMLFORMATS_OFFICEDOCUMENT_WORDPROCESSINGML_DOCUMENT,
  ],
  [FILE_TYPE_KEYS.PDF]: [APPLICATION_FORMATS.PDF],
  [FILE_TYPE_KEYS.TXT]: Object.values(TEXT_FORMATS),
  [FILE_TYPE_KEYS.VIDEO]: Object.values(VIDEO_FORMATS),
};

const supportedFileTypes = Object.values(fileTypes).flat();

const handleContentTypeQuery = (formats: FileMediaFormat[], contentTypeQuery: ContentTypeQuery) => {
  if (!contentTypeQuery) return false;

  const isFormat = createMemberGuard(formats).predicate;

  if (isFormat(contentTypeQuery)) return true;

  const extensions = formats.flatMap((format) => MEDIA_FORMAT_EXTENSIONS[format]);

  return extensions.some((extension) => contentTypeQuery.includes(extension));
};

const isFileType = (key: FileTypeKey, contentTypeQuery: ContentTypeQuery) =>
  handleContentTypeQuery(fileTypes[key], contentTypeQuery);

const isSupportedFileType = (contentTypeQuery: ContentTypeQuery) =>
  handleContentTypeQuery(supportedFileTypes, contentTypeQuery);

const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      if (reader.error) return;

      const base64data = reader.result as string;
      resolve(base64data.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
  });

const filesToBase64 = async (files: File[]) =>
  Promise.all(
    files.map(async (file) => ({
      content: await toBase64(file),
      name: file.name,
    })),
  );

const downloadBlob = async (blob: Blob, fileName: string): Promise<void> => {
  const link = document.createElement('a');
  link.style.display = 'none';
  document.body.appendChild(link);

  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(link.href);
  document.body.removeChild(link);
};

const downloadUsingUri = (fileUri: string, fileName?: string) => {
  if (fileName) {
    fetch(fileUri)
      .then((response) => response.blob())
      .then((blob) => downloadBlob(blob, fileName));

    return;
  }

  const a = document.createElement('a');
  a.href = fileUri;
  a.rel = 'noopener noreferrer';
  a.target = '_blank';

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const generateDownloadName = (name = 'default_fileName', extension: string | null = null) => {
  if (!extension) return name;

  return name.replace(/\..+$/, '') + extension;
};

const downloadFile = async (file: FileDownloadOptions): Promise<void> => {
  const { id, fileBytes, fileUri, fileName, contentType, extension } = file;

  if (!id && !fileBytes) {
    downloadUsingUri(fileUri, fileName);
    return;
  }

  try {
    const fileBytesData = fileBytes || (id ? await fileApi.downloadFile(id) : '');
    const binaryString = atob(fileBytesData as string);

    const bytes = new Uint8Array(Array.from(binaryString, (char) => char.charCodeAt(0)));

    const blob = new Blob([bytes], { type: contentType });
    const downloadURL = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadURL;
    link.download = generateDownloadName(fileName, extension);
    link.click();
    link.remove();

    URL.revokeObjectURL(downloadURL);
  } catch (error) {
    downloadUsingUri(file.fileUri, file.fileName);
  }
};

const getBlobFileName = (contentDisposition: string) => {
  let fileName = 'default_fileName';
  if (contentDisposition) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
  }
  return fileName;
};

const validatePdfFile = async (file: File): Promise<boolean> => {
  // Check if file is a PDF
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    return true; // Not a PDF, skip validation
  }

  try {
    // Read the first 4 bytes to check for PDF header
    const arrayBuffer = await file.slice(0, 4).arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const header = String.fromCharCode(...uint8Array);

    // Valid PDFs should start with %PDF
    if (header.startsWith('%PDF')) {
      return true;
    }

    // Some PDFs might have whitespace or BOM before the header, check first 1024 bytes
    const largerBuffer = await file.slice(0, 1024).arrayBuffer();
    const largerArray = new Uint8Array(largerBuffer);
    const largerText = String.fromCharCode(...largerArray);

    return largerText.includes('%PDF');
  } catch (error) {
    // If we can't read the file, consider it corrupted
    return false;
  }
};

const validateUblXmlFile = async (file: File): Promise<boolean> => {
  // Accept XML or UBL mime types
  const allowedTypes = ['application/xml', 'text/xml', 'application/ubl+xml'];

  const isXml =
    allowedTypes.includes(file.type) ||
    file.name.toLowerCase().endsWith('.xml') ||
    file.name.toLowerCase().endsWith('.ubl');

  if (!isXml) return true; // Not XML → skip validation

  try {
    const text = await file.text();

    // Try to parse as XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, 'application/xml');

    // Check for parser errors
    if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
      return false; // invalid XML
    }

    // Check for valid UBL document types
    const validRootTags = [
      'Invoice',
      'CreditNote',
      'Order',
      'OrderResponse',
      'ApplicationResponse',
    ];

    const root = xmlDoc.documentElement.tagName;

    if (!validRootTags.includes(root)) {
      return false; // Not a UBL document
    }

    // Optional: verify UBL namespace
    const ns = xmlDoc.documentElement.getAttribute('xmlns');
    if (ns && !ns.includes('urn:oasis:names:specification:ubl')) {
      return false;
    }

    return true; // valid UBL/XML
  } catch (err) {
    return false;
  }
};

export {
  downloadBlob,
  downloadFile,
  downloadUsingUri,
  fileTypes,
  filesToBase64,
  getBlobFileName,
  generateDownloadName,
  isFileType,
  isSupportedFileType,
  supportedFileTypes,
  toBase64,
  validatePdfFile,
  validateUblXmlFile,
};
